import React from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { SITE } from "../lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Quali servizi offrite?",
  "Lavorate fuori Genova?",
  "Posso avere un preventivo?",
  "Come funziona il cappotto termico?",
];

function getSession() {
  let s = localStorage.getItem("edil-chat-session");
  if (!s) {
    s = `web-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
    localStorage.setItem("edil-chat-session", s);
  }
  return s;
}

export default function ChatBot() {
  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState([
    {
      role: "assistant",
      content:
        "Ciao! 👋 Sono l'assistente AI di Edil Murgia. Posso darti informazioni su servizi, tempistiche o aiutarti a richiedere un preventivo. Come posso esserti utile?",
    },
  ]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const sessionId = React.useMemo(getSession, []);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: msg }, { timeout: 60000 });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Mi dispiace, c'è stato un problema (${detail}). Puoi chiamarci al ${SITE.phones[0]}.`,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-24 right-5 z-50 h-14 w-14 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-105"
        aria-label="Apri chatbot"
        data-testid="chatbot-toggle"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && (
          <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-brand-sage border-2 border-white animate-pulse-soft" />
        )}
      </button>

      {open && (
        <div
          className="fixed bottom-44 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-[400px] h-[560px] max-h-[75vh] card-tactile shadow-2xl flex flex-col overflow-hidden"
          data-testid="chatbot-panel"
        >
          <div className="px-5 py-4 border-b border-brand-line bg-gradient-to-br from-brand-bg to-white flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-brand-terracotta text-white flex items-center justify-center font-bold heading-display">
              MA
            </div>
            <div className="flex-1">
              <div className="font-semibold text-brand-ink">MurgiAI · Edil Murgia</div>
              <div className="text-xs text-brand-mute flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-brand-sage" /> Online · risposta immediata
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-brand-bg/40" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-brand-terracotta text-white rounded-br-md"
                      : "bg-white border border-brand-line text-brand-ink rounded-bl-md"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-brand-line rounded-2xl rounded-bl-md px-3.5 py-3 flex items-center gap-2 text-brand-mute">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> sto scrivendo...
                </div>
              </div>
            )}
          </div>

          {messages.length <= 2 && !busy && (
            <div className="px-3 pb-2 flex flex-wrap gap-2 border-t border-brand-line bg-white">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 rounded-full bg-brand-bg border border-brand-line hover:border-brand-terracotta hover:text-brand-terracotta transition-colors"
                  data-testid={`chat-suggestion-${s.slice(0, 10)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-brand-line bg-white p-3 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi un messaggio..."
              className="flex-1 rounded-full border border-brand-line px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-terracotta/30 focus:border-brand-terracotta"
              disabled={busy}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="h-10 w-10 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white flex items-center justify-center disabled:opacity-50"
              data-testid="chat-send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
